<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.report_event_members") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="loading || !eventId" @click="exportXlsx">{{ $t("pos.common.export") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<card class="no-border-card">
				<div class="row align-items-end">
					<div class="col-md-4 col-12 mb-2">
						<label class="form-control-label d-block">{{ $t("pos.report.mid_event") }} *</label>
						<el-select v-model="eventId" filterable class="w-100" :placeholder="$t('pos.report.mid_event_pick')" @change="load">
							<el-option v-for="e in events" :key="e.id" :label="e.name" :value="e.id"></el-option>
						</el-select>
					</div>
					<div class="col-md-4 col-12 mb-2">
						<label class="form-control-label d-block">{{ $t("pos.report.mid_search") }}</label>
						<el-input v-model="q" clearable :placeholder="$t('pos.report.mid_search')" @keyup.enter.native="load" @clear="load"></el-input>
					</div>
					<div class="col-md-2 col-6 mb-2">
						<el-checkbox v-model="includeTest" @change="load">{{ $t("pos.filter.include_test") }}</el-checkbox>
					</div>
					<div class="col-md-2 col-6 mb-2 text-right">
						<base-button size="sm" type="primary" :disabled="!eventId" @click="load">{{ $t("pos.common.search") }}</base-button>
					</div>
				</div>
			</card>

			<card v-if="!eventId">
				<p class="text-muted mb-0">{{ $t("pos.report.mid_event_required") }}</p>
			</card>
			<!-- table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0, everything else in px-4 -->
			<card v-else v-loading="loading" body-classes="px-0">
				<div class="px-4">
					<div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
						<div>
							<h3 class="mb-0">{{ event.name }}</h3>
							<small class="text-muted">{{ event.start_date }} – {{ event.end_date }}</small>
						</div>
						<a v-if="event.board_url" :href="event.board_url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">{{ $t("pos.report.open_board") }}</a>
					</div>
					<div class="pos-sum-strip row text-center my-2">
						<div class="col"><div class="text-muted small">{{ $t("pos.report.members") }}</div><div class="h3 mb-0">{{ int(totals.members) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.report.bills") }}</div><div class="h3 mb-0">{{ int(totals.bill_count) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.report.total") }}</div><div class="h3 mb-0">{{ money(totals.total) }}</div></div>
					</div>
				</div>
				<el-table :data="rows" header-row-class-name="thead-light" class="bon-table pos-table pos-click-rows" @row-click="open">
					<el-table-column :label="$t('pos.report.rank')" prop="rank" width="80" align="right"></el-table-column>
					<el-table-column :label="$t('pos.report.mid')" prop="mid_text" width="110"></el-table-column>
					<el-table-column :label="$t('pos.report.bill_count')" width="110" align="right">
						<template slot-scope="{ row }">{{ int(row.bill_count) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.items')" width="80" align="right">
						<template slot-scope="{ row }">{{ int(row.item_qty) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.total_spent')" min-width="130" align="right">
						<template slot-scope="{ row }"><strong>{{ money(row.total) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.avg_bill')" min-width="120" align="right">
						<template slot-scope="{ row }">{{ money(row.avg_bill) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.phone')" prop="phone" width="125" class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.report.name')" prop="name" min-width="130"></el-table-column>
					<el-table-column :label="$t('pos.report.first_bill')" prop="first_bill_at" width="175" class-name="pos-nowrap"></el-table-column>
					<el-table-column :label="$t('pos.report.last_bill')" prop="last_bill_at" width="175" class-name="pos-nowrap"></el-table-column>
				</el-table>
			</card>
		</div>

		<!-- B47 drill-down -->
		<el-dialog :title="$t('pos.report.mid_bills', { mid: detail.mid_text })" :visible.sync="detailVisible" width="760px" top="5vh" custom-class="pos-mid-drawer">
			<div v-loading="detailLoading">
				<div class="pos-sum-strip row text-center mb-3">
					<div class="col"><div class="text-muted small">{{ $t("pos.report.bill_count") }}</div><div class="h3 mb-0">{{ int(detail.summary.bill_count) }}</div></div>
					<div class="col"><div class="text-muted small">{{ $t("pos.report.total_spent") }}</div><div class="h3 mb-0">{{ money(detail.summary.total) }}</div></div>
					<div class="col"><div class="text-muted small">{{ $t("pos.report.avg_bill") }}</div><div class="h3 mb-0">{{ money(detail.summary.avg_bill) }}</div></div>
				</div>
				<el-collapse v-model="openBills">
					<el-collapse-item v-for="b in detail.bills" :key="b.id" :name="b.id">
						<template slot="title">
							<span :class="b.status === 9 ? 'pos-voided-text' : ''">
								<strong class="mr-2">{{ b.bill_no }}</strong>
								<span class="text-muted mr-2">{{ b.created_at }}</span>
								<span class="mr-2">{{ money(b.total) }}</span>
							</span>
							<el-tag v-if="b.status === 9" type="danger" size="mini">{{ $t("pos.bill.voided_tag") }}</el-tag>
						</template>
						<div class="small text-muted mb-1">{{ b.device_code }} · {{ b.seller_name }} · {{ methodLabel(b.payment_method) }}</div>
						<div v-if="b.status === 9 && b.void_reason" class="small text-danger mb-1">{{ $t("pos.bill.void_reason") }}: {{ b.void_reason }}</div>
						<table class="table table-sm mb-0">
							<tbody>
								<tr v-for="(it, i) in b.items" :key="i">
									<td>
										{{ it.name }}
										<div v-for="(c, j) in it.components || []" :key="j" class="small text-muted pl-3">· {{ c.name }} × {{ c.qty }}</div>
									</td>
									<td class="text-right">× {{ it.qty }}</td>
									<td class="text-right">{{ money(it.line_amount) }}</td>
								</tr>
							</tbody>
						</table>
					</el-collapse-item>
				</el-collapse>
			</div>
			<span slot="footer">
				<base-button type="secondary" @click="detailVisible = false">{{ $t("pos.common.close") }}</base-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import { utils, writeFile } from "xlsx";
import { Table, TableColumn, Select, Option, Input, Checkbox, Dialog, Collapse, CollapseItem, Tag } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt, posExportRows, POS_EXPORT_MAX_ROWS } from "@/utils/pos";
import posPageMixin from "./posPageMixin";
import PosInstallAlert from "./PosInstallAlert.vue";

const emptyDetail = () => ({ mid_text: "", summary: {}, bills: [] });

export default {
	name: "PosReportEventMembers",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosInstallAlert,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[Select.name]: Select,
		[Option.name]: Option,
		[Input.name]: Input,
		[Checkbox.name]: Checkbox,
		[Dialog.name]: Dialog,
		[Collapse.name]: Collapse,
		[CollapseItem.name]: CollapseItem,
		[Tag.name]: Tag
	},
	data() {
		return {
			eventId: "",
			q: "",
			includeTest: false,
			event: {},
			rows: [],
			totals: {},
			detailVisible: false,
			detailLoading: false,
			detail: emptyDetail(),
			openBills: []
		};
	},
	computed: {
		events() {
			return this.$store.getters["Pos/events"] || [];
		}
	},
	mounted() {
		this.$store
			.dispatch("Pos/fetchLookups")
			.then(() => this.applyQuery())
			.catch((err) => this.posError(err));
	},
	watch: {
		// kept-alive: a later hand-off from Top Spender (new query) re-applies; ignore
		// route changes to other pages (#447: never act on a route that is not ours)
		"$route.query"() {
			if (this.$route.name === "PosReportEventMembers") this.applyQuery();
		}
	},
	methods: {
		// ?event_id=&mid=&include_test= (hand-off from PosReportTopSpenders): load the
		// event and open that buyer's bills
		applyQuery() {
			const q = this.$route.query || {};
			if (!q.event_id) return;
			this.eventId = Number(q.event_id);
			if (q.include_test !== undefined) this.includeTest = q.include_test === "1" || q.include_test === 1 || q.include_test === true;
			this.load();
			if (q.mid) this.open({ mid_text: String(q.mid) });
		},
		money: posMoney,
		int: posInt,
		methodLabel(m) {
			return m === "transfer" ? this.$t("pos.bill.transfer") : this.$t("pos.bill.cash");
		},
		params(extra) {
			const p = { event_id: this.eventId, include_test: this.includeTest ? 1 : 0 };
			if (this.q.trim()) p.q = this.q.trim();
			return { ...p, ...(extra || {}) };
		},
		async load() {
			if (!this.eventId) return;
			this.loading = true;
			try {
				const res = await PosService.reportEventMembers(this.params());
				const d = (res.data && res.data.Datum) || {};
				this.event = d.event || {};
				this.rows = d.rows || [];
				this.totals = d.totals || {};
				this.notInstalled = false;
			} catch (err) {
				this.rows = [];
				this.totals = {};
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		async open(row) {
			this.detail = { ...emptyDetail(), mid_text: row.mid_text };
			this.openBills = [];
			this.detailVisible = true;
			this.detailLoading = true;
			try {
				const res = await PosService.reportEventMember(row.mid_text, { event_id: this.eventId });
				const d = (res.data && res.data.Datum) || {};
				this.detail = { mid_text: d.mid_text || row.mid_text, summary: d.summary || {}, bills: d.bills || [] };
				if (this.detail.bills.length) this.openBills = [this.detail.bills[0].id];
			} catch (err) {
				this.detailVisible = false;
				this.posError(err);
			} finally {
				this.detailLoading = false;
			}
		},
		async exportXlsx() {
			if (!this.eventId) return;
			this.loading = true;
			try {
				const res = await PosService.reportEventMembers(this.params({ export: 1 }));
				const d = (res.data && res.data.Datum) || {};
				const rows = d.rows || [];
				if (rows.length >= POS_EXPORT_MAX_ROWS) this.posToast("pos.common.export_capped");
				const t = (k) => this.$t(k);
				const columns = [
					{ label: t("pos.report.rank"), value: "rank" },
					{ label: t("pos.report.mid"), value: "mid_text" },
					{ label: t("pos.report.bill_count"), value: "bill_count" },
					{ label: t("pos.report.items"), value: "item_qty" },
					{ label: t("pos.report.total_spent"), value: (r) => Number(r.total) },
					{ label: t("pos.report.avg_bill"), value: (r) => Number(r.avg_bill) },
					{ label: t("pos.report.phone"), value: "phone" },
					{ label: t("pos.report.name"), value: "name" },
					{ label: t("pos.report.first_bill"), value: "first_bill_at" },
					{ label: t("pos.report.last_bill"), value: "last_bill_at" }
				];
				const wb = utils.book_new();
				utils.book_append_sheet(wb, utils.json_to_sheet(posExportRows(columns, rows)), t("pos.report.members").slice(0, 31));
				writeFile(wb, `pos-event-members-${this.eventId}.xlsx`);
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style>
.pos-voided-text {
	text-decoration: line-through;
	color: #525f7f;
}
</style>
